/**
 * Term — Wikipedia-style hover definition tooltip.
 *
 * Uses a React portal to render the tooltip at document.body level.
 * This means no parent stacking context (sidebar, header, overflow)
 * can ever clip or cover it.
 *
 * On hover/focus, the component measures its own position and places
 * the tooltip where there's room:
 *   - Default: above, horizontally centered
 *   - Near top edge: flips below
 *   - Near left/right edge: shifts to stay within viewport
 *   - Mobile (<640px): centered card in middle of viewport
 *
 * The <dfn> retains data-definition for print styles (CSS @media print
 * reads it via attr() to render definitions inline on paper).
 */

import { useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface TermProps {
  def: string;
  children: ReactNode;
}

const TOOLTIP_MAX_WIDTH = 300;
const TOOLTIP_GAP = 10;
const VIEWPORT_PAD = 16;
const MOBILE_BREAKPOINT = 640;

export default function Term({ def, children }: TermProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const termRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Track mobile state
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const show = useCallback(() => {
    if (!termRef.current) return;
    setIsVisible(true);

    // Defer measurement to next frame so tooltip is in DOM
    requestAnimationFrame(() => {
      if (!termRef.current) return;
      const rect = termRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Measure tooltip once it exists
      const tooltipEl = tooltipRef.current;
      const tooltipWidth = tooltipEl ? tooltipEl.offsetWidth : Math.min(TOOLTIP_MAX_WIDTH, vw - VIEWPORT_PAD * 2);
      const tooltipHeight = tooltipEl ? tooltipEl.offsetHeight : 80;

      // Vertical: prefer above, flip below if not enough room
      const spaceAbove = rect.top;
      const below = spaceAbove < tooltipHeight + TOOLTIP_GAP + VIEWPORT_PAD;
      const top = below
        ? rect.bottom + TOOLTIP_GAP
        : rect.top - tooltipHeight - TOOLTIP_GAP;

      // Horizontal: center on term, then clamp to viewport
      let left = rect.left + rect.width / 2 - tooltipWidth / 2;
      left = Math.max(VIEWPORT_PAD, Math.min(left, vw - tooltipWidth - VIEWPORT_PAD));

      setPos({ top, left, below });
    });
  }, []);

  const hide = useCallback(() => {
    setIsVisible(false);
    setPos(null);
  }, []);

  // Re-measure on scroll while visible (for scroll containers)
  useEffect(() => {
    if (!isVisible) return;
    const handleScroll = () => {
      if (isVisible) show();
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isVisible, show]);

  const tooltip = isVisible && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={tooltipRef}
          className={`term-tooltip ${isVisible ? 'is-visible' : ''} ${isMobile ? 'is-mobile' : ''}`}
          style={
            isMobile
              ? undefined // Mobile uses CSS fixed centering
              : pos
                ? { top: pos.top, left: pos.left }
                : { top: 0, left: -9999 } // Off-screen while measuring
          }
          role="tooltip"
        >
          {def}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <dfn
        ref={termRef}
        className="term"
        data-definition={def}
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        {children}
      </dfn>
      {tooltip}
    </>
  );
}
